import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '../../_hooks/use-auth'
import { ExtensionAuthState } from './types'
import { readExtensionId, sendHandoffToExtension } from './helpers'
import {
  PageContainer,
  ContentCard,
  Wordmark,
  Heading,
  Subheading,
  PrimaryButton,
  ErrorBanner,
  StatusIndicator,
  Spinner,
  SuccessMark,
} from './styled'

/**
 * The sign-in handoff page the QuickPass browser extension opens. Once the
 * renter is signed in to the web app, it hands the extension the Firebase
 * refresh token (over the externally-connectable channel) so the extension can
 * mint its own ID tokens without bundling the Firebase SDK. The renter signs in
 * with any method here (email/Google/magic-link); the extension never sees a
 * password.
 *
 * @returns {JSX.Element}
 */
const ExtensionAuthPage = (): JSX.Element => {
  const { firebaseUser, isAuthenticated, isLoading } = useAuth()
  const navigate = useNavigate()
  const handoffAttemptedReference = useRef<boolean>(false)
  const [state, setState] = useState<ExtensionAuthState>({
    status: 'checking',
    errorMessage: '',
  })

  useEffect(() => {
    const extensionId = readExtensionId()

    if (extensionId === null) {
      setState({
        status: 'error',
        errorMessage: 'Open this page from the QuickPass extension.',
      })
      return
    }

    if (isLoading === true) {
      setState({ status: 'checking', errorMessage: '' })
      return
    }

    if (isAuthenticated === false || firebaseUser === null) {
      setState({ status: 'needs-sign-in', errorMessage: '' })
      return
    }

    if (handoffAttemptedReference.current === true) {
      return
    }
    handoffAttemptedReference.current = true

    setState({ status: 'connecting', errorMessage: '' })

    const runHandoff = async (): Promise<void> => {
      const result = await sendHandoffToExtension(
        extensionId,
        firebaseUser.refreshToken,
        firebaseUser.uid,
        firebaseUser.email ?? '',
      )

      if (result.ok === true) {
        setState({ status: 'connected', errorMessage: '' })
        return
      }

      if (result.error === 'no-extension') {
        setState({ status: 'no-extension', errorMessage: '' })
        return
      }

      setState({
        status: 'error',
        errorMessage: result.error ?? 'We could not connect the extension.',
      })
    }

    void runHandoff()
  }, [firebaseUser, isAuthenticated, isLoading])

  const handleSignIn = useCallback((): void => {
    navigate('/sign-in')
  }, [navigate])

  return (
    <PageContainer>
      <ContentCard>
        <Wordmark>QuickPass</Wordmark>

        {state.status === 'checking' ? (
          <>
            <Heading>Connecting</Heading>
            <Subheading>Checking your QuickPass session&hellip;</Subheading>
            <StatusIndicator>
              <Spinner
                animate={{
                  rotate: 360,
                  transition: {
                    repeat: Infinity,
                    duration: 0.7,
                    ease: 'linear',
                  },
                }}
              />
            </StatusIndicator>
          </>
        ) : null}

        {state.status === 'needs-sign-in' ? (
          <>
            <Heading>Sign in to connect</Heading>
            <Subheading>
              Sign in to QuickPass, then click Connect in the extension again to
              finish linking it to your account.
            </Subheading>
            <PrimaryButton type="button" onClick={handleSignIn}>
              Sign in to QuickPass
            </PrimaryButton>
          </>
        ) : null}

        {state.status === 'connecting' ? (
          <>
            <Heading>Connecting the extension</Heading>
            <Subheading>Linking the extension to your account&hellip;</Subheading>
            <StatusIndicator>
              <Spinner
                animate={{
                  rotate: 360,
                  transition: {
                    repeat: Infinity,
                    duration: 0.7,
                    ease: 'linear',
                  },
                }}
              />
            </StatusIndicator>
          </>
        ) : null}

        {state.status === 'connected' ? (
          <>
            <SuccessMark>✓</SuccessMark>
            <Heading>You&apos;re connected</Heading>
            <Subheading>
              The QuickPass extension is linked to your account. You can close
              this tab and start saving listings.
            </Subheading>
          </>
        ) : null}

        {state.status === 'no-extension' ? (
          <>
            <Heading>Extension not found</Heading>
            <Subheading>
              We couldn&apos;t reach the QuickPass extension. Make sure it&apos;s
              installed and enabled, then click Connect again.
            </Subheading>
          </>
        ) : null}

        {state.status === 'error' ? (
          <>
            <Heading>Couldn&apos;t connect</Heading>
            <ErrorBanner role="alert">{state.errorMessage}</ErrorBanner>
          </>
        ) : null}
      </ContentCard>
    </PageContainer>
  )
}

export default ExtensionAuthPage
